import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";

export default function Offers() {
  const [coupons, setCoupons] = useState([]);
  const [brands, setBrands] = useState([]);
  const [onSale, setOnSale] = useState([]);

  useEffect(() => {
    api.get("/coupons/active").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/products").then((r) => setOnSale(r.data.filter((p) => p.on_sale).slice(0, 12))).catch(() => {});
  }, []);

  const copy = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success(`${code} copied — apply it in your bag`));
  };

  return (
    <main data-testid="offers-page">
      <Navbar />
      <section className="pt-32 md:pt-40 pb-16 relative overflow-hidden">
        <div className="spotlight absolute inset-0" aria-hidden="true" />
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative z-10">
          <p className="text-xs uppercase tracking-[0.5em] text-gold mb-4">Offers & Coupons</p>
          <h1 className="font-serif font-medium text-5xl sm:text-6xl text-zinc-900 tracking-tight" data-testid="offers-heading">
            Little <em className="text-gold">luxuries</em>, less money.
          </h1>
          <p className="text-zinc-500 mt-5 max-w-lg">Apply a code in your bag before payment. One coupon per order.</p>
        </div>
      </section>
      <section className="pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {coupons.length === 0 ? (
            <p className="font-serif italic text-2xl text-zinc-400" data-testid="offers-empty">No live offers right now — check back soon.</p>
          ) : (
            coupons.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="border border-pink-100 bg-gradient-to-br from-pink-50/70 to-white p-8 flex items-center gap-6"
                data-testid={`offer-card-${c.code}`}
              >
                <div className="w-12 h-12 border border-gold/30 bg-white flex items-center justify-center text-gold shrink-0">
                  <Tag size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono font-bold text-lg text-zinc-900 tracking-wider">{c.code}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] bg-gold text-white px-2.5 py-1">
                      {c.kind === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-500 mt-2">{c.description}</p>
                  {c.min_order > 0 && <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1.5">Min order {inr(c.min_order)}</p>}
                </div>
                <button
                  onClick={() => copy(c.code)}
                  data-testid={`offer-copy-${c.code}`}
                  className="shrink-0 flex items-center gap-2 border border-gold/40 text-gold text-[10px] uppercase tracking-[0.25em] px-5 py-3 hover:bg-gold hover:text-white transition-colors duration-300"
                >
                  <Copy size={12} /> Copy
                </button>
              </motion.div>
            ))
          )}
        </div>
        {onSale.length > 0 && (
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-20" data-testid="on-sale-section">
            <div className="flex items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.5em] text-red-500 mb-3">Limited Time</p>
                <h2 className="font-serif font-medium text-3xl sm:text-4xl text-zinc-900 tracking-tight">On Sale <em className="text-gold">Now</em></h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
              {onSale.map((p, i) => (
                <ProductCard key={p.id} product={p} brandName={brands.find((b) => b.slug === p.brand_slug)?.name || p.brand_slug} index={i} />
              ))}
            </div>
          </div>
        )}
        <div className="text-center mt-16">
          <Link to="/shop" data-testid="offers-shop-link" className="inline-block bg-gold text-white text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-champagne transition-colors duration-300">
            Shop the Collection
          </Link>
        </div>
      </section>
      <Footer brands={brands} />
    </main>
  );
}
